/**
 * IslamicCalendar — Hijri-aware "what is today, religiously?" detector.
 *
 * Memoriz-domain knowledge (detecting Eid, Ramadan, Mawlid, Ashura,
 * Laylat al-Qadr, etc. from a Hijri date is Islamic knowledge, not life-
 * scheduling logic). Lives as a leaf service so Yawmi (the life scheduler)
 * can consume it without owning Islamic logic.
 *
 * Boundary: see docs/yawmi-vs-memoriz-boundary.md.
 *   - Memoriz-domain (this file): WHAT day is it Islamically?
 *   - Yawmi-domain (consumers): WHEN to nudge the user, HOW to surface it.
 *
 * Zero network. Computed from `HijriDate` + `Date.getDay()`.
 *
 * Long-term path: when standalone Memoriz app ships its API, this file
 * becomes a thin client around `Memoriz.getCurrentSpecialDay()`. The
 * interface stays stable so Yawmi never breaks.
 */

import { gregorianToHijri, type HijriDate } from './HijriDate';

export type IslamicSpecialDay =
  | 'eid_fitr'        // Hijri 1 Shawwāl (and the 2 days after)
  | 'eid_fitr_eve'    // Last day of Ramadan (29 or 30 Ramaḍān)
  | 'eid_adha'        // Hijri 10-13 Dhū al-Ḥijjah
  | 'arafah'          // Hijri 9 Dhū al-Ḥijjah
  | 'ramadan_eve'     // Hijri 28-30 Shaʿbān (last 3 days)
  | 'ramadan'         // Hijri 1-30 Ramaḍān
  | 'laylat_qadr'     // Hijri 27 Ramaḍān (most-attested odd night)
  | 'mawlid'          // Hijri 12 Rabīʿ al-Awwal
  | 'ashura'          // Hijri 10 Muḥarram
  | 'islamic_new_year'// Hijri 1 Muḥarram
  | 'jumuah';         // Friday — recurs weekly

export interface SpecialDayInfo {
  /** Stable id — Yawmi maps this to its UI prompts and to Memoriz template lookups. */
  id: IslamicSpecialDay;
  /** True only on the day itself. Eve states have their own ids. */
  isToday: boolean;
  /** Hijri date components (cached so callers don't re-compute). */
  hijri: HijriDate;
  /** Day of week 0-6 (0=Sun, 5=Fri). */
  dayOfWeek: number;
  /** Local hour 0-23 — used by callers to differentiate morning/evening windows. */
  hour: number;
  /** Stable priority for the priority engine. Higher = more prominent. */
  weight: number;
}

/**
 * Detect the most relevant Islamic special day for `now`. Returns null on
 * an ordinary day. Note: only ONE special day is returned (the highest-
 * priority one) — Eid takes precedence over a normal Friday, etc.
 */
export function getCurrentSpecialDay(now: Date = new Date()): SpecialDayInfo | null {
  const hijri = gregorianToHijri(now);
  const dayOfWeek = now.getDay();
  const hour = now.getHours();

  const base = (id: IslamicSpecialDay, isToday: boolean, weight: number): SpecialDayInfo => ({
    id, isToday, hijri, dayOfWeek, hour, weight,
  });

  // Eid al-Fitr — Hijri 1 Shawwāl (day) + 2 spillover days where greetings
  // are still socially appropriate.
  if (hijri.month === 10 && hijri.day === 1) return base('eid_fitr', true, 100);
  if (hijri.month === 10 && (hijri.day === 2 || hijri.day === 3)) return base('eid_fitr', false, 90);

  // Eid al-Fitr eve — last day of Ramadan
  if (hijri.month === 9 && hijri.day >= 29) return base('eid_fitr_eve', true, 95);

  // Eid al-Adha — Hijri 10-13 Dhū al-Ḥijjah
  if (hijri.month === 12 && hijri.day === 10) return base('eid_adha', true, 100);
  if (hijri.month === 12 && hijri.day >= 11 && hijri.day <= 13) return base('eid_adha', false, 88);

  // Arafah day — Hijri 9 Dhū al-Ḥijjah (the day before Eid al-Adha)
  if (hijri.month === 12 && hijri.day === 9) return base('arafah', true, 90);

  // Laylat al-Qadr — most-attested odd night, Hijri 27 Ramaḍān
  // (other odd nights of last 10 are also candidates, but 27 is the strongest).
  if (hijri.month === 9 && hijri.day === 27) return base('laylat_qadr', true, 92);

  // Ramadan eve — last 3 days of Shaʿbān
  if (hijri.month === 8 && hijri.day >= 28) return base('ramadan_eve', true, 80);

  // Ramadan — any day during the month
  if (hijri.month === 9) return base('ramadan', true, 75);

  // Islamic new year — Hijri 1 Muḥarram
  if (hijri.month === 1 && hijri.day === 1) return base('islamic_new_year', true, 70);

  // Ashura — Hijri 10 Muḥarram
  if (hijri.month === 1 && hijri.day === 10) return base('ashura', true, 70);

  // Mawlid an-Nabi — Hijri 12 Rabīʿ al-Awwal (some madhabs don't celebrate;
  // weight is moderate, callers may suppress per user preference).
  if (hijri.month === 3 && hijri.day === 12) return base('mawlid', true, 60);

  // Friday — recurs every week, lower priority than the dated specials.
  if (dayOfWeek === 5) {
    // Higher weight in the morning window when greeting cards are sent.
    const w = (hour >= 6 && hour < 14) ? 50 : 35;
    return base('jumuah', true, w);
  }

  return null;
}

/** Convenience: detect Friday alone (most common consumer query). */
export function isJumuahDay(now: Date = new Date()): boolean {
  return now.getDay() === 5;
}

/** Convenience: are we anywhere in the Ramadan month? */
export function isRamadan(now: Date = new Date()): boolean {
  return gregorianToHijri(now).month === 9;
}

/** Convenience: are we in the Hajj season (Dhū al-Ḥijjah 1-13)? */
export function isHajjSeason(now: Date = new Date()): boolean {
  const h = gregorianToHijri(now);
  return h.month === 12 && h.day <= 13;
}
